import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Ellipsis } from '@/components/ui/ellipsis';
import { getGeographyFlag, getGeographyLabel } from '@/shared/geography';
import type { FC } from 'react';

const MAX_FLAGS = 3;

export type MultiGeographyBadgeProps = {
  geography: string;
  institutionGeographies?: string[];
  institutionName?: string | null;
  /** Badge height class, defaults to "h-auto" */
  className?: string;
};

export const MultiGeographyBadge: FC<MultiGeographyBadgeProps> = ({
  geography,
  institutionGeographies,
  institutionName,
  className,
}) => {
  let geos: string[];
  if (institutionGeographies && institutionGeographies.length > 0) {
    geos = institutionGeographies.filter(g => g !== 'world');
    if (geos.length === 0) {
      if (geography === 'world') {
        geos = ['world'];
      } else if (geography && geography !== 'unknown') {
        geos = [geography];
      } else {
        geos = [];
      }
    }
  } else if (geography === 'world') {
    geos = ['world'];
  } else if (geography && geography !== 'unknown') {
    geos = [geography];
  } else {
    geos = [];
  }

  if (geos.length === 0) return null;

  // Single geography: flag + label
  if (geos.length === 1) {
    const flagImage = getGeographyFlag(geos[0]!);
    return (
      <span
        className={`inline-flex items-center gap-[6px] px-2 py-[3px] rounded-lg text-[12px] font-medium text-neutral-900 bg-neutral-50 border border-neutral-200 ${className ?? ''}`}
      >
        {flagImage && (
          <img src={flagImage} alt="" className="w-4 h-4 object-contain" />
        )}
        <Ellipsis lineClamp={1} hasTooltip>
          {getGeographyLabel(geos[0]!)}
        </Ellipsis>
      </span>
    );
  }

  // Multiple geographies: flags (max 3) + "+N" with tooltip
  const visible = geos.slice(0, MAX_FLAGS);
  const overflow = geos.length - MAX_FLAGS;
  const geoLabels = geos.map(g => getGeographyLabel(g)).join(', ');
  const fullLabel = institutionName
    ? `${institutionName}: ${geoLabels}`
    : geoLabels;

  const badge = (
    <span
      className={`inline-flex items-center gap-1 px-2 py-[3px] rounded-lg text-[12px] font-medium text-neutral-900 bg-neutral-50 border border-neutral-200 ${className ?? ''}`}
    >
      {visible.map(g => {
        const flagImage = getGeographyFlag(g);
        return flagImage ? (
          <img
            key={g}
            src={flagImage}
            alt={getGeographyLabel(g)}
            className="w-4 h-3 object-contain"
          />
        ) : null;
      })}
      {overflow > 0 && (
        <span className="text-[11px] text-neutral-500">+{overflow}</span>
      )}
    </span>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent>{fullLabel}</TooltipContent>
    </Tooltip>
  );
};
